// noinspection DuplicatedCode
import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Button from "@material-tailwind/react/Button";

import {
  getFaceEnrollmentList,
  deleteFaceEnrollment,
  clearFaceEnrollmentMessage,
  faceEnrollmentSelector,
} from "./faceEnrollmentSlice";

import { Colors } from "../../../../assets/Colors";
import Loading from "../../../../components/loading/Loading";
import { loadKioskModels } from "../../../../lib/faceRecognition";
import EnrollWizard from "./EnrollWizard";
import TandaTanganModal from "./TandaTanganModal";
import { ambilGambarTandaTangan } from "./tandaTanganApi";

// Indikator kualitas enrollment (dihitung backend, annotateEnrollmentQuality):
// sebaran sampel > SPREAD_MAX = ada sampel buruk / salah orang; karyawan lain
// termirip < MIRIP_MAX = berisiko tertukar di kiosk (ambang kiosk 0.5).
const SPREAD_MAX = 0.45;
const MIRIP_MAX = 0.45;

function KualitasBadge({ row }) {
  if (!row.Terdaftar) return <span className="text-blueGray-400">—</span>;
  if (row.SebaranSampel > SPREAD_MAX) {
    return (
      <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs" title={`Sebaran sampel ${row.SebaranSampel} (batas ${SPREAD_MAX})`}>
        Sampel tidak konsisten — enroll ulang
      </span>
    );
  }
  if (row.MiripJarak !== undefined && row.MiripJarak < MIRIP_MAX) {
    return (
      <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded text-xs"
        title={`Jarak ${row.MiripJarak} ke ${row.MiripNama} (${row.MiripNik}); kiosk mengenali sbg orang sama di bawah 0,5`}>
        Mirip {row.MiripNama} ({row.MiripJarak})
      </span>
    );
  }
  return <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">Baik</span>;
}

// Pratinjau tanda tangan di kolom tabel. Gambar butuh header Authorization → diambil sbg blob;
// dimuat ulang saat TandaTanganTanggal berubah (ganti/hapus lewat modal).
function TandaTanganThumb({ row }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!row.TandaTangan) {
      setSrc(null);
      return undefined;
    }
    let batal = false;
    let url = null;
    ambilGambarTandaTangan(row.Nik).then((u) => {
      if (batal) {
        if (u) URL.revokeObjectURL(u);
        return;
      }
      url = u;
      setSrc(u);
    });
    return () => {
      batal = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [row.Nik, row.TandaTangan, row.TandaTanganTanggal]);

  if (!row.TandaTangan) return <span className="text-blueGray-400">—</span>;
  if (!src) return <span className="text-blueGray-400">…</span>;
  return (
    <img
      src={src}
      alt={`Tanda tangan ${row.NamaKaryawan}`}
      title={`Disimpan ${row.TandaTanganTanggal}`}
      className="mx-auto h-10 max-w-[120px] object-contain rounded border border-solid border-blueGray-100 bg-white p-0.5"
    />
  );
}

// 5 sampel per karyawan (variasi pose & jarak) — nearest-neighbor lintas
// sampel saat matching di kiosk jauh lebih tahan variasi pose/cahaya drpd 1
// sampel rata2. Pengambilan terpandu & tersimpan atomik: lihat EnrollWizard.

// Tombol kolom Aksi: ukuran seragam supaya baris rapi (Enroll / Tanda Tangan / Hapus Wajah).
const BTN =
  "inline-flex items-center justify-center h-8 w-28 rounded border text-xs font-semibold whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

const FaceEnrollmentPage = () => {
  const dispatch = useDispatch();
  const {
    data,
    pagination,
    isGetFetching,
    isDeleteFetching,
    message,
    errorMessage,
  } = useSelector(faceEnrollmentSelector);

  const [searchInput, setSearchInput] = useState("");
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [modalTarget, setModalTarget] = useState(null); // {Nik, NamaKaryawan}
  const [ttdTarget, setTtdTarget] = useState(null); // baris untuk modal Tanda Tangan (terpisah dari enroll wajah)
  const [modelsReady, setModelsReady] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);

  const fetchData = useCallback(
    (page = 1) => {
      dispatch(getFaceEnrollmentList({ search: searchInput || undefined, page, page_size: 10 }));
    },
    [dispatch, searchInput]
  );

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitialLoad && !isGetFetching) setIsInitialLoad(false);
  }, [isGetFetching, isInitialLoad]);

  useEffect(() => {
    if (message) {
      toast.success(message, { position: "bottom-right" });
      dispatch(clearFaceEnrollmentMessage());
    }
    if (errorMessage) {
      toast.error(errorMessage, { position: "bottom-right" });
      dispatch(clearFaceEnrollmentMessage());
    }
  }, [message, errorMessage, dispatch]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData(1);
  };

  const openEnrollModal = async (row) => {
    setModalTarget(row);
    setModelsReady(false);
    setModelsLoading(true);
    try {
      // Model ringan yang sama dgn kiosk (2026-10-07): modal lebih cepat
      // terbuka & wajah didaftarkan dgn pipeline yang dipakai kiosk.
      await loadKioskModels();
      setModelsReady(true);
    } catch (e) {
      toast.error("Gagal memuat model face recognition: " + (e.message || e), {
        position: "bottom-right",
      });
    } finally {
      setModelsLoading(false);
    }
  };

  const closeModal = () => {
    setModalTarget(null);
  };

  const handleDeleteAll = async (row) => {
    if (!window.confirm(`Hapus semua sampel wajah ${row.NamaKaryawan}? Karyawan ini perlu didaftarkan ulang dari awal.`)) {
      return;
    }
    try {
      await dispatch(deleteFaceEnrollment(row.Nik)).unwrap();
      fetchData(pagination.currentPage);
    } catch (e) {
      // toast sudah muncul lewat errorMessage effect
    }
  };

  if (isInitialLoad && isGetFetching) {
    return <Loading />;
  }

  return (
    <>
      <div className="md:ml-64">
        <div
          style={{ background: Colors.primary }}
          className="pt-14 pb-16 px-3 md:px-8 h-auto"
        >
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
          </div>
        </div>

        <div className="px-3 md:px-8 h-auto -mt-24">
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 px-4">
              <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded">
                <div className="rounded-t mb-0 px-4 py-3 border-0">
                  <div className="flex flex-wrap items-center">
                    <div className="relative w-full px-4 max-w-full flex-grow flex-1">
                      <h3 className="font-semibold text-lg text-blueGray-700">
                        Enrollment Wajah Karyawan
                      </h3>
                      <p className="text-sm text-blueGray-400 mb-2">
                        Daftarkan wajah tiap karyawan (5 sampel) sebelum bisa dipakai
                        di kiosk absensi wajah. Matching wajah berjalan di browser —
                        foto/video tidak pernah dikirim ke server, hanya data
                        numerik hasil pemrosesan yang disimpan. Tombol <b>Tanda Tangan</b>
                        di kolom Aksi menyimpan tanda tangan digital karyawan (PNG
                        transparan) — terpisah dari enroll wajah.
                      </p>
                      <form onSubmit={handleSearch} className="flex gap-2 mt-2">
                        <input
                          type="text"
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          placeholder="Cari Nik / Nama / PIN..."
                          className="border rounded px-3 py-2 text-sm w-64"
                        />
                        <Button color="lightBlue" buttonType="filled" size="sm" ripple="light" type="submit">
                          Cari
                        </Button>
                      </form>
                    </div>
                  </div>
                </div>

                <div className="block w-full overflow-x-auto">
                  <table className="items-center w-full bg-transparent border-collapse">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-xs uppercase font-semibold text-left bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100">Nik</th>
                        <th className="px-4 py-3 text-xs uppercase font-semibold text-left bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100">PIN</th>
                        <th className="px-4 py-3 text-xs uppercase font-semibold text-left bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100">Nama Karyawan</th>
                        <th className="px-4 py-3 text-xs uppercase font-semibold text-center bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100">Sampel</th>
                        <th className="px-4 py-3 text-xs uppercase font-semibold text-center bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100">Status</th>
                        <th className="px-4 py-3 text-xs uppercase font-semibold text-center bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100">Kualitas</th>
                        <th className="px-4 py-3 text-xs uppercase font-semibold text-center bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100">Tanda Tangan</th>
                        <th className="px-4 py-3 text-xs uppercase font-semibold text-center bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100" style={{ minWidth: 380 }}>Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.length === 0 && (
                        <tr>
                          <td colSpan={8} className="text-center py-6 text-sm text-blueGray-400">
                            Tidak ada data
                          </td>
                        </tr>
                      )}
                      {data.map((row) => (
                        <tr key={row.Nik}>
                          <td className="border-t-0 px-4 py-3 text-xs whitespace-nowrap p-4">{row.Nik}</td>
                          <td className="border-t-0 px-4 py-3 text-xs whitespace-nowrap p-4">{row.Pin || "-"}</td>
                          <td className="border-t-0 px-4 py-3 text-xs whitespace-nowrap p-4">{row.NamaKaryawan}</td>
                          <td className="border-t-0 px-4 py-3 text-xs whitespace-nowrap p-4 text-center">{row.JumlahSampel}</td>
                          <td className="border-t-0 px-4 py-3 text-xs whitespace-nowrap p-4 text-center">
                            {row.Terdaftar ? (
                              <span className="bg-green-200 text-green-800 px-2 py-1 rounded text-xs">Terdaftar</span>
                            ) : (
                              <span className="bg-orange-200 text-orange-800 px-2 py-1 rounded text-xs">Belum</span>
                            )}
                          </td>
                          <td className="border-t-0 px-4 py-3 text-xs whitespace-nowrap p-4 text-center">
                            <KualitasBadge row={row} />
                          </td>
                          <td className="border-t-0 px-4 py-3 text-xs whitespace-nowrap p-4 text-center">
                            <TandaTanganThumb row={row} />
                          </td>
                          <td className="border-t-0 px-4 py-3 text-xs p-4">
                            <div className="flex flex-wrap items-center justify-center gap-2">
                              <button type="button" onClick={() => openEnrollModal(row)} className={`${BTN} bg-blue-500 border-blue-500 text-white hover:bg-blue-600`}>
                                {row.Terdaftar ? "Enroll Ulang" : "Enroll Wajah"}
                              </button>
                              <button
                                type="button"
                                onClick={() => setTtdTarget(row)}
                                title={row.TandaTangan ? `Tanda tangan terpasang (${row.TandaTanganTanggal})` : "Belum ada tanda tangan"}
                                className={`${BTN} ${
                                  row.TandaTangan
                                    ? "bg-green-50 border-green-500 text-green-700 hover:bg-green-100"
                                    : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                                }`}
                              >
                                {row.TandaTangan ? "✓ Tanda Tangan" : "Tanda Tangan"}
                              </button>
                              {row.Terdaftar && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteAll(row)}
                                  disabled={isDeleteFetching}
                                  title="Hapus semua sampel wajah karyawan ini"
                                  className={`${BTN} bg-white border-red-300 text-red-600 hover:bg-red-50`}
                                >
                                  Hapus Wajah
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-between items-center px-4 py-3">
                  <span className="text-xs text-blueGray-400">
                    Halaman {pagination.currentPage} dari {pagination.totalPages || 1} ({pagination.totalCount} karyawan)
                  </span>
                  <div className="flex gap-2">
                    <Button
                      color="blueGray"
                      buttonType="outline"
                      size="sm"
                      disabled={!pagination.hasPrev || isGetFetching}
                      onClick={() => fetchData(pagination.currentPage - 1)}
                    >
                      Sebelumnya
                    </Button>
                    <Button
                      color="blueGray"
                      buttonType="outline"
                      size="sm"
                      disabled={!pagination.hasNext || isGetFetching}
                      onClick={() => fetchData(pagination.currentPage + 1)}
                    >
                      Berikutnya
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {ttdTarget && (
        <TandaTanganModal
          key={ttdTarget.Nik}
          target={ttdTarget}
          onClose={() => setTtdTarget(null)}
          onSaved={() => {
            setTtdTarget(null);
            fetchData(pagination.currentPage);
          }}
        />
      )}
      {modalTarget && modelsLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl px-6 py-4 text-sm text-gray-600">Memuat model face recognition…</div>
        </div>
      )}
      {modalTarget && modelsReady && (
        <EnrollWizard
          key={modalTarget.Nik}
          target={modalTarget}
          onClose={closeModal}
          onEnrollOther={(row) => openEnrollModal(row)}
          onSaved={() => {
            closeModal();
            fetchData(pagination.currentPage);
          }}
        />
      )}
    </>
  );
};

export default FaceEnrollmentPage;
